// src/features/live/LivePage.tsx — placeholder Live page (web).
import { useMemo } from 'react';
import { NetworkEntry } from '@/features/networks/NetworkEntry';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { Button, DisabledHint } from '@/ui';
import { useLive } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { LiveChips } from './LiveChips';
import { LiveChannelCard } from './LiveChannelCard';
import { FeaturedLive } from './FeaturedLive';
import { LiveChatPanel } from './LiveChatPanel';
import { PreviewNotice } from '@/components/PreviewNotice';
import { TopLiveCreatorsPanel } from './panels/TopLiveCreatorsPanel';
import { LiveCategoriesPanel } from './panels/LiveCategoriesPanel';
import { LiveNowPanel } from './panels/LiveNowPanel';
import { filterLive } from './filterLive';
import { WEB_CHIPS, PREVIEW_NOTICE } from './meta';
import type { LiveChipId, LiveCreator } from '@/core/models/live';

const VALID_CHIP_IDS = new Set<string>(WEB_CHIPS.map((c) => c.id));

/** Validates the ?chip= query param against the known chip list — an unknown or
 *  missing value falls back to 'all' instead of half-selecting nothing. */
function normalizeChip(raw: string | null): LiveChipId {
  return raw !== null && VALID_CHIP_IDS.has(raw) ? (raw as LiveChipId) : 'all';
}

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SectionHeading = styled.h2`
  margin: 22px 0 12px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  ${media.tabletDown`grid-template-columns: 1fr;`}
`;

const ChannelsRow = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: thin;

  & > * {
    flex: 0 0 220px;
  }
`;

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

function creatorMap(creators: LiveCreator[]): Record<string, LiveCreator> {
  const map: Record<string, LiveCreator> = {};
  for (const c of creators) map[c.id] = c;
  return map;
}

export function LivePage() {
  return <NetworkEntry family="events_live" destination="live" fallback={<LivePreview />} />;
}
function LivePreview() {
  // The URL is the single source of truth for the active chip — see
  // VideosPage's identical pattern (B2 fix: a prior version that only read
  // searchParams inside a useState initializer silently ignored URL changes
  // after mount, e.g. clicking a LiveCategoriesPanel link while already on /live).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeChip = normalizeChip(searchParams.get('chip'));
  const { channels, creators, loaded } = useLive();

  function handleChipChange(id: LiveChipId) {
    setSearchParams(id === 'all' ? {} : { chip: id });
  }

  const byId = useMemo(() => creatorMap(creators), [creators]);

  // The featured block only makes sense as a global recommendation — showing it
  // fixed regardless of the active chip (a Music channel headlining while
  // Gaming is selected) would read as broken, not curated. Mirrors VideosPage.
  const featured = useMemo(
    () => (activeChip === 'all' ? (channels.find((c) => c.featured) ?? channels[0]) : undefined),
    [channels, activeChip],
  );

  const visible = useMemo(
    () => filterLive(channels, { chip: activeChip }).filter((c) => c.id !== featured?.id),
    [channels, activeChip, featured],
  );

  const liveChannelsRow = visible.slice(0, 5);

  // The right rail's "Live now" panel is a persistent, chip-independent
  // surface (same convention as Videos' TopCreatorsPanel/ShortsPanel), so it
  // does NOT re-filter by activeChip the way liveChannelsRow does. It
  // COMPLEMENTS the default ('all' chip) row instead of duplicating it
  // (slice-4-xl fix round F3 — a prior version independently took "the global
  // featured-excluded list, first 5", which is the exact same 5 channels
  // liveChannelsRow shows on 'all' in the exact same order, so live-07/08
  // were unreachable anywhere on the page and the rail was pure duplication).
  // S8 still applies: the featured channel itself is excluded (it's the hero).
  const globalFeaturedId = useMemo(
    () => (channels.find((c) => c.featured) ?? channels[0])?.id,
    [channels],
  );
  const nonFeaturedGlobal = useMemo(
    () => channels.filter((c) => c.id !== globalFeaturedId),
    [channels, globalFeaturedId],
  );
  const liveNowRail = useMemo(() => nonFeaturedGlobal.slice(5), [nonFeaturedGlobal]);

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="live">
        <RailPanel id="live-now" label="Live now">
          <LiveNowPanel channels={liveNowRail} />
        </RailPanel>
        <RailPanel id="categories" label="Live categories">
          <LiveCategoriesPanel />
        </RailPanel>
        <RailPanel id="creators" label="Top live creators">
          <TopLiveCreatorsPanel creators={creators} />
        </RailPanel>
      </RailGroup>
    ),
    [liveNowRail, creators],
  );
  useRightRail(rightRail);

  return (
    <section>
      <NetworkLinks destination="live" />
      <Header>
        <HeaderText>
          <Title>Live</Title>
          <Subtitle>Real people. Real conversations. Live.</Subtitle>
        </HeaderText>
        <DisabledHint explanation="Going live isn't available yet.">
          {(describedBy) => (
            <Button
              type="button"
              variant="primary"
              aria-disabled="true"
              aria-describedby={describedBy}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Go Live
            </Button>
          )}
        </DisabledHint>
      </Header>

      <PreviewNotice text={PREVIEW_NOTICE} />

      <LiveChips chips={WEB_CHIPS} active={activeChip} onChange={handleChipChange} />

      {loaded && featured && (
        <FeaturedGrid>
          <FeaturedLive channel={featured} creator={byId[featured.authorId]} />
          <LiveChatPanel messages={featured.chat} />
        </FeaturedGrid>
      )}

      <SectionHeading>Live channels</SectionHeading>
      {liveChannelsRow.length === 0 ? (
        <Empty>Nothing live in this category yet.</Empty>
      ) : (
        <ChannelsRow data-testid="live-channels-row">
          {liveChannelsRow.map((c) => (
            <LiveChannelCard key={c.id} channel={c} creator={byId[c.authorId]} />
          ))}
        </ChannelsRow>
      )}
    </section>
  );
}
